/* eslint-disable react/jsx-indent-props */
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as R from 'ramda';

import {
    Row,
    Col,
    SearchSelect as NativeSearchSelect,
    LoadingDots,
    FormGroup,
    FormGroupLabel,
} from '@app/ui';
import classNames from '@app/ui/sports-components/utils/classNames';
import SearchSelect from '@app/ui/sports-components/SearchSelect';
import Button from '@app/ui/sports-components/Button';

import AccordionTitle from '@app/ui/sports-components/AccordionTitle';
import AccordionIcon from '@app/ui/sports-components/AccordionIcon';
import AccordionChildItems from '@app/ui/sports-components/AccordionChildItems';
import AccordionItem from '@app/ui/sports-components/AccordionItem';
import {
    getSelectedValues,
    toSearchSelectOptions,
    toSelectedObject,
} from '@app/utils/multiSelectValue';

import SelectedFiltersTags from '../components/filters/SelectedFiltersTags';

import connect from '../utils/connect';
import { shortDate } from '../utils/formatters';

import { InVenueContext } from './InVenue.context';

const Filters = () => {
    const { t } = useTranslation();
    const {
        isLoadingFilters,
        rightsholderOptions,
        seasonOptions,
        gameDateOptions,
        sectionOptions,
        current,
        isLoadingGameDates,
        isLoadingSections,
        isLoadingSeasons,
        isLoadingRightsholders,
        isLoadingAllRightsholders,
        setFieldValue,
        handleSubmit,
        values,
        isExpanded,
        setIsExpanded,
        hasNoData,
    } = useContext(InVenueContext);

    const {
        rightsholder: selectedRightsholder,
        season: selectedSeason,
        gameDates: selectedGameDates,
        sections: selectedSectionIds,
    } = current;

    const disableRighsholder =
        isLoadingFilters || isLoadingRightsholders || isLoadingAllRightsholders;
    const disableSeason =
        isLoadingFilters ||
        isLoadingSeasons ||
        !values.rightsholder ||
        disableRighsholder;
    const disableGameDates =
        isLoadingFilters ||
        isLoadingGameDates ||
        !values.season ||
        disableSeason;
    const disableSections =
        isLoadingFilters ||
        isLoadingSections ||
        !values.season ||
        disableSeason;
    const disableSubmit = isLoadingFilters || !values.season || disableSeason;

    const selectedFiltersElements = [
        selectedRightsholder && {
            title: t('filters.rightsholder'),
            elements: [selectedRightsholder.name],
        },
        selectedSeason && {
            title: t('filters.season'),
            elements: [`${selectedSeason.year}`],
        },
        {
            title: t('filters.eventDate'),
            elements: selectedGameDates.map(date => shortDate(date)),
        },
        {
            title: t('filters.section'),
            elements:
                selectedSectionIds?.length &&
                selectedRightsholder &&
                selectedSeason
                    ? selectedSectionIds.map(section => section.section)
                    : sectionOptions.map(({ label }) => label),
        },
    ].filter(p => p);

    const hasFilters =
        !isLoadingFilters &&
        selectedFiltersElements.some(({ elements }) => elements.length > 0);

    const triggerCollapse = () => {
        if (isLoadingFilters) return;
        setIsExpanded(!isExpanded);
    };

    const areFiltersEmpty =
        R.isEmpty(rightsholderOptions) || R.isEmpty(gameDateOptions);

    const filterTitleClass = classNames('filter-title -color-bg-white', {
        '-p-b-0': !areFiltersEmpty,
    });

    useEffect(() => {
        if (hasNoData && !isLoadingFilters) {
            setIsExpanded(true);
        }
    }, [hasNoData, isLoadingFilters]);

    return (
        <AccordionItem
            className="filter-box"
            data-test="new-filters-container"
            isActive={isExpanded}
        >
            <AccordionTitle
                className={filterTitleClass}
                size="sm"
                context="secondary"
                onClick={triggerCollapse}
                data-test="collapse-new-filters"
            >
                {hasFilters && (
                    <Row gutter={16}>
                        <Col
                            md={2}
                            className="gds-text--body-md filter-title-text"
                        >
                            {t('filters.title')}
                        </Col>
                        <Col md={22}>
                            <SelectedFiltersTags
                                selectedFiltersArray={selectedFiltersElements}
                            />
                        </Col>
                    </Row>
                )}
                {isLoadingFilters && <LoadingDots />}
            </AccordionTitle>

            <AccordionIcon
                size="sm"
                className="--cursor-pointer"
                onClick={triggerCollapse}
            />

            <AccordionChildItems
                className="-p-h-3 -p-v-3"
                data-test={'new-filters-children'}
                hidden={!isExpanded}
            >
                <form onSubmit={handleSubmit}>
                    <Row gutter={16}>
                        <Col md={6}>
                            <FormGroup
                                layout="vertical"
                                data-test="new-rh-dropdown"
                                disabled={disableRighsholder}
                            >
                                <FormGroupLabel
                                    text={t('filters.rightsholder')}
                                />
                                <SearchSelect
                                    name="rightsholder"
                                    size="sm"
                                    placeholder={t('filters.rightsholder')}
                                    options={rightsholderOptions}
                                    onChange={v =>
                                        setFieldValue('rightsholder', v)
                                    }
                                    value={values.rightsholder}
                                    hideClearButton
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup
                                layout="vertical"
                                data-test="new-season-dropdown"
                                disabled={disableSeason}
                            >
                                <FormGroupLabel text={t('filters.season')} />
                                <SearchSelect
                                    name="season"
                                    size="sm"
                                    placeholder={t('filters.season')}
                                    options={seasonOptions}
                                    onChange={v => setFieldValue('season', v)}
                                    value={values.season}
                                    hideClearButton
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup
                                layout="vertical"
                                data-test="new-gd-dropdown"
                                disabled={disableGameDates}
                            >
                                <FormGroupLabel text={t('filters.eventDate')} />
                                <NativeSearchSelect
                                    mode="multiple"
                                    name="gameDates"
                                    size="sm"
                                    placeholder={t('filters.eventDate')}
                                    options={toSearchSelectOptions(
                                        gameDateOptions,
                                    )}
                                    value={getSelectedValues(
                                        gameDateOptions,
                                        values.gameDates,
                                    )}
                                    onChange={v =>
                                        setFieldValue(
                                            'gameDates',
                                            toSelectedObject(v),
                                        )
                                    }
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup
                                layout="vertical"
                                data-test="new-section-dropdown"
                                disabled={disableSections}
                            >
                                <FormGroupLabel text={t('filters.section')} />
                                <NativeSearchSelect
                                    mode="multiple"
                                    name="sections"
                                    size="sm"
                                    placeholder={t('filters.section')}
                                    options={toSearchSelectOptions(
                                        sectionOptions,
                                    )}
                                    value={getSelectedValues(
                                        sectionOptions,
                                        values.sections,
                                    )}
                                    onChange={v =>
                                        setFieldValue(
                                            'sections',
                                            toSelectedObject(v),
                                        )
                                    }
                                />
                            </FormGroup>
                        </Col>
                        <Col md={24}>
                            <Button
                                type="submit"
                                size="sm"
                                context="primary"
                                className="-m-t-3"
                                disabled={disableSubmit}
                            >
                                {t('filters.updateFilters')}
                            </Button>
                        </Col>
                    </Row>
                </form>
            </AccordionChildItems>
        </AccordionItem>
    );
};

export default connect(['client'])(Filters);
